import axios from 'axios';
import { bus } from '@/main';
import root from '@/_helpers/roots';
import toast from '@/services/toast';
import {
    getAxiosErrorMessage,
    getResponseData
} from '@/utils/chat/messageUtils';

const UPLOAD_MESSAGE_FILE_URL =
    root.uploadMsgFileUrl.link;

export default {
    data() {
        return {
            uploadFile: null,
            filename: '',
            imgSrc: '',
            previewObjectUrl: '',
            fileMessage: '',
            suggestionUserListStateForFileAttach:
                false,
            submitted: false,
            listLoading: false,
            uploadProgress: 0,
            uploadRequestCancel: null
        };
    },

    methods: {
        handleFileMessageInput(event) {
            const value = event.target.value;

            this.fileMessage = value;

            this.suggestionUserListStateForFileAttach =
                Boolean(
                    this.selectedRoomInfo &&
                    this.selectedRoomInfo
                        .is_group_chat &&
                    /(^|\s)@[^\s@]*$/.test(value)
                );
        },

        handleFileMessageKeydown(event) {
            if (
                event.key !== 'PageUp' &&
                event.key !== 'PageDown'
            ) {
                return;
            }

            const cursorPosition =
                event.key === 'PageUp'
                    ? 0
                    : event.target.value.length;

            event.preventDefault();

            event.target.setSelectionRange(
                cursorPosition,
                cursorPosition
            );
        },

        onInputClick(event) {
            if (event && event.target) {
                event.target.value = '';
            }
        },

        handleFileUpload(event) {
            const files =
                event &&
                event.target &&
                event.target.files;

            if (!files || !files.length) {
                return;
            }

            this.setUploadFile(
                files[0],
                files[0].name
            );
        },

        captureImgOnPasteMethod(event) {
            const items =
                event.clipboardData &&
                event.clipboardData.items;

            if (!items) {
                return;
            }

            for (
                let index = 0;
                index < items.length;
                index += 1
            ) {
                if (
                    items[index].type.indexOf(
                        'image/'
                    ) !== 0
                ) {
                    continue;
                }

                const file =
                    items[index].getAsFile();

                if (!file) {
                    continue;
                }

                const mimeExtension = (
                    file.type.split('/')[1] ||
                    'png'
                ).replace('jpeg', 'jpg');

                const filename =
                    file.name ||
                    (
                        `pasted-image-${Date.now()}.` +
                        mimeExtension
                    );

                event.preventDefault();
                this.setUploadFile(file, filename);
                return;
            }
        },

        setUploadFile(file, filename) {
            this.releasePreviewObjectUrl();
            this.uploadFile = file;
            this.filename =
                filename ||
                file.name ||
                'attachment';

            this.imgSrc = '';

            if (
                !file ||
                file.type.indexOf('image/') !== 0
            ) {
                return;
            }

            const urlObject = this.getURLObj();

            if (
                urlObject &&
                typeof urlObject.createObjectURL ===
                    'function'
            ) {
                this.previewObjectUrl =
                    urlObject.createObjectURL(file);

                this.imgSrc = this.previewObjectUrl;
                return;
            }

            if (typeof FileReader === 'function') {
                const reader = new FileReader();

                reader.onload = (readerEvent) => {
                    if (this.uploadFile === file) {
                        this.imgSrc =
                            readerEvent.target.result;
                    }
                };

                reader.readAsDataURL(file);
            }
        },

        getURLObj() {
            if (typeof window === 'undefined') {
                return null;
            }

            return window.URL || window.webkitURL;
        },

        releasePreviewObjectUrl() {
            if (!this.previewObjectUrl) {
                return;
            }

            const urlObject = this.getURLObj();

            if (
                urlObject &&
                typeof urlObject.revokeObjectURL ===
                    'function'
            ) {
                urlObject.revokeObjectURL(
                    this.previewObjectUrl
                );
            }

            this.previewObjectUrl = '';
        },

        resetUploadState() {
            this.releasePreviewObjectUrl();
            this.uploadFile = null;
            this.filename = '';
            this.imgSrc = '';
            this.fileMessage = '';

            this.suggestionUserListStateForFileAttach =
                false;

            this.listLoading = false;
            this.uploadProgress = 0;
            this.submitted = false;
        },

        cancelUploadFile() {
            if (this.uploadRequestCancel) {
                this.uploadRequestCancel.cancel(
                    'File upload cancelled.'
                );

                this.uploadRequestCancel = null;
            }

            this.resetUploadState();
        },

        getAxiosErrorMessage(
            error,
            fallbackMessage
        ) {
            return getAxiosErrorMessage(
                error,
                fallbackMessage
            );
        },

        fileMsgSubmitMethod() {
            if (
                this.submitted ||
                !this.uploadFile ||
                !this.selectedRoomInfo
            ) {
                return Promise.resolve();
            }

            const roomId = String(
                this.selectedRoomInfo.chat_room
            );

            const formData = new FormData();
            const source =
                axios.CancelToken.source();

            formData.append(
                'chat_room_user',
                this.selectedRoomInfo.chat_room
            );

            formData.append(
                'message',
                this.fileMessage.trim()
            );

            formData.append(
                'file',
                this.uploadFile
            );

            formData.append(
                'filename',
                this.filename
            );

            this.uploadRequestCancel = source;
            this.submitted = true;
            this.listLoading = true;
            this.uploadProgress = 0;

            return axios.post(
                UPLOAD_MESSAGE_FILE_URL,
                formData,
                {
                    cancelToken: source.token,

                    onUploadProgress:
                        (progressEvent) => {
                            if (progressEvent.total) {
                                this.uploadProgress =
                                    Math.round(
                                        (
                                            progressEvent
                                                .loaded /
                                            progressEvent
                                                .total
                                        ) * 100
                                    );
                            }
                        }
                }
            )
                .then((response) => {
                    if (
                        roomId !==
                        String(
                            this.selectedActvRoomId
                        )
                    ) {
                        return;
                    }

                    const uploadedMessage =
                        getResponseData(response);

                    const messageId =
                        uploadedMessage.id;

                    const socketNotified =
                        messageId !== undefined &&
                        messageId !== null &&
                        this.sendChatSocketPayload({
                            type: 'file_upload',
                            message_id: messageId
                        });

                    if (!socketNotified) {
                        this.allMessageListMethod();
                    }

                    bus.$emit('newChatMessage', {
                        chatRoomId:
                            this.selectedRoomInfo
                                .chat_room
                    });
                })
                .catch((error) => {
                    if (!axios.isCancel(error)) {
                        toast.error(
                            getAxiosErrorMessage(
                                error,
                                'File upload failed. Please try again.'
                            )
                        );
                    }
                })
                .then(() => {
                    if (
                        this.uploadRequestCancel ===
                        source
                    ) {
                        this.uploadRequestCancel = null;
                        this.resetUploadState();
                    }
                });
        }
    }
};
